import type { STChecklistItem, STDocument, STMissingReason, STOpenFolderState } from '@st/sdk'
import { match, P } from '@st/pattern'

export function checklistAttachedDocuments(item: STChecklistItem, documents: STDocument[]) {
  // Binder order is shared by Intake and Delivery; repeated references render once.
  return documents.filter((document) =>
    item.attachments.some((reference) => reference.documentId === document.id)
  )
}

export function checklistMissingReasons(
  context: STChecklistItem['context'],
  reasons: Record<string, STMissingReason[]>
) {
  return match(context)
    .with({ type: 'document_request' }, (c) => reasons[c.documentTypeId] ?? [])
    .with({ type: 'questionnaire_entry' }, (c) => reasons[c.categoryId] ?? [])
    .with({ type: 'tax_payment' }, () => [])
    .exhaustive()
}

type DocumentSection = {
  heading: { id: string; name: string }
  items: { id: string; name: string }[]
}
export function checklistAttachmentModel(
  sections: DocumentSection[],
  attachments: STChecklistItem['attachments']
) {
  const documents = sections.flatMap((section) => section.items)
  return {
    attached: attachments.map((reference) => ({
      reference,
      key: `${reference.documentId}:${reference.location}`,
      label: match(reference.documentId)
        .with('organizer', () => `Organizer · ${reference.location}`)
        .otherwise(
          (id) => documents.find((document) => document.id === id)?.name ?? 'Document unavailable'
        )
    })),
    available: sections.flatMap((section) =>
      section.items
        .filter(
          (document) => !attachments.some((reference) => reference.documentId === document.id)
        )
        .map((document) => ({
          id: document.id,
          label: document.name,
          group: section.heading.name,
          searchText: `${section.heading.name} ${document.name}`
        }))
    )
  }
}

export function checklistRows(
  items: STChecklistItem[],
  documents: STDocument[],
  reasons: Record<string, STMissingReason[]>
) {
  return items.map((item) => ({
    item,
    documents: checklistAttachedDocuments(item, documents),
    incompleteReason: checklistMissingReasons(item.context, reasons).find(
      (reason) => reason.key == item.incompleteReason
    )
  }))
}

export function documentRequestCanSave(
  editor: Extract<import('./model').ChecklistEditor, { kind: 'document_request' }>
) {
  return editor.draft.context.documentTypeId != ''
}

export function checklistPaymentAmount(item: Pick<STChecklistItem, 'context'>) {
  return match(item.context)
    .with({ type: 'tax_payment' }, ({ amount }) => {
      if (amount === '') return undefined
      return new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(
        Number(amount)
      )
    })
    .otherwise(() => undefined)
}

export function checklistItemDialogModel(
  dialog: Extract<import('./model').ChecklistDialog, { mode: 'attachments' | 'complete' }>
) {
  const { item } = dialog
  if (dialog.mode == 'attachments')
    return {
      title: 'Manage attachments',
      action: 'Done',
      emptyLabel: 'No attachments yet',
      name: item.name,
      note: undefined
    }
  const actions = match(item.status)
    .with('complete', () => ({
      title: 'Reopen checklist item',
      action: 'Reopen item',
      emptyLabel: 'No attachments yet'
    }))
    .with('incomplete', () => ({
      title: 'Complete checklist item',
      action: 'Mark complete',
      emptyLabel: 'Consider attaching supporting documents before marking this item complete.'
    }))
    .exhaustive()
  return {
    ...actions,
    name: item.name,
    note: match(item.context.type)
      .with('document_request', () => item.note)
      .with(P.union('tax_payment', 'questionnaire_entry'), () => undefined)
      .exhaustive()
  }
}

export function deliveryChecklistEmptyState(
  generation: STOpenFolderState['deliveryChecklistGeneration']
) {
  const instructions =
    'This checklist will tell the client which tax payments to make and how to make them.'
  // The checklist activates once the client can see the tax return.
  return match(generation)
    .with('awaiting_publication', () => ({ kind: 'locked' as const }))
    .with('ready', () => ({
      kind: 'empty' as const,
      canAutoGenerate: true,
      instructions
    }))
    .with('no_return_analysis', () => ({
      kind: 'empty' as const,
      canAutoGenerate: false,
      instructions
    }))
    .exhaustive()
}
